import { useState, useEffect, useCallback, useMemo } from "react";
import ErrorBoundary from "../../../components/ErrorBoundary";
import RegistrosFilters, { RegistrosFilterState } from './components/RegistrosFilters/RegistrosFilters';
import RegistrosTable from './components/RegistrosTable/RegistrosTable';
import Pagination from '../../../components/Pagination/Pagination';
import LiquidarContratosModal, { ContratoParaLiquidar } from './components/LiquidarContratosModal/LiquidarContratosModal';
import CancelarRegistrosModal, { RegistroParaCancelar } from './components/CancelarRegistrosModal/CancelarRegistrosModal';
import { registrosService, RegistroAquisicao, StatusRegistro, CarteiraFundo, podeSerLiquidado, podeSerCancelado, computeDisplayStatus, mapDisplayStatusToApiStatus, STATUS_REGISTROS } from './services/registrosService';
import './style.css';
import '../../../styles/filters-card.css';

const getTodayDate = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const Registros = () => {
    const [filters, setFilters] = useState<RegistrosFilterState>({
        numeroContrato: '',
        carteiras: '',
        dataAquisicao: getTodayDate(),
        status: ''
    });

    const [registros, setRegistros] = useState<RegistroAquisicao[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    const [carteiras, setCarteiras] = useState<CarteiraFundo[]>([]);
    const [loadingCarteiras, setLoadingCarteiras] = useState(false);
    
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [sortField, setSortField] = useState<string>('');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    const [showLiquidarModal, setShowLiquidarModal] = useState(false);
    const [contratosToLiquidar, setContratosToLiquidar] = useState<ContratoParaLiquidar[]>([]);

    const [showCancelarModal, setShowCancelarModal] = useState(false);
    const [registrosToCancel, setRegistrosToCancel] = useState<RegistroParaCancelar[]>([]);

    const handleFilterChange = (field: keyof RegistrosFilterState, value: string) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const clearFilters = () => {
        setFilters({
            numeroContrato: '',
            carteiras: '',
            dataAquisicao: getTodayDate(),
            status: ''
        });
        setCurrentPage(0);
    };

    const fetchCarteiras = useCallback(async () => {
        try {
            setLoadingCarteiras(true);
            const data = await registrosService.fetchCarteirasFundos();
            setCarteiras(data);
        } catch (err) {
            console.error('Erro ao carregar carteiras:', err);
            setError('Erro ao carregar lista de carteiras');
        } finally {
            setLoadingCarteiras(false);
        }
    }, []);

    const fetchRegistros = useCallback(async () => {

        try {
            setLoading(true);
            setError(null);
            const carteirasArray = [filters.carteiras];
            const statusArray = filters.status ? mapDisplayStatusToApiStatus(filters.status) : undefined;

            const sortParam = sortField
                ? `${sortField === 'status' ? 'statusCalculado' : sortField},${sortDirection}`
                : undefined;
            
            const response = await registrosService.fetchRegistros({
                numeroContrato: filters.numeroContrato || '',
                carteiras: carteirasArray,
                dataAquisicao: filters.dataAquisicao || undefined,
                status: statusArray,
                page: currentPage,
                size: pageSize,
                sort: sortParam
            });
            
            if (Array.isArray(response)) {
                setRegistros(response);
                setTotalPages(1);
                setTotalElements(response.length);
            } else {
                setRegistros(response.content || []);
                setTotalPages(response.totalPages || 1);
                setTotalElements(response.totalElements || 0);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erro ao buscar registros');
            console.error('Erro ao buscar registros:', err);
        } finally {
            setLoading(false);
        }
    }, [filters, currentPage, pageSize, sortField, sortDirection]);

    const [availableStatuses, setAvailableStatuses] = useState<StatusRegistro[]>([...STATUS_REGISTROS]);

    const fetchAvailableStatuses = useCallback(async () => {
        try {
            const carteirasArray = filters.carteiras ? [filters.carteiras] : [];
            const contagem = await registrosService.fetchStatusContagem(
                filters.dataAquisicao || getTodayDate(),
                carteirasArray
            );
            const statusMap: Record<string, StatusRegistro> = {
                'Aceita':             'ATIVO',
                'REGISTRADO':         'ATIVO',
                'LIQUIDADO_PARCIALMENTE': 'ATIVO',
                'ATIVO':              'ATIVO',
                'ERRO':               'RECUSADO',
                'REJEITADO':          'RECUSADO',
                'RECUSADO':           'RECUSADO',
                'CANCELADO':          'CANCELADO',
                'LIQUIDADO':          'LIQUIDADO',
                'NAO_REGISTRADO':     'NAO_REGISTRADO',
            };
            const seen = new Set<StatusRegistro>();
            contagem.forEach(({ status, quantidade }) => {
                if (quantidade > 0 && statusMap[status]) {
                    seen.add(statusMap[status]);
                }
            });
            const filteredStatuses = STATUS_REGISTROS.filter(s => seen.has(s));
            setAvailableStatuses(filteredStatuses);
        } catch {
            setAvailableStatuses([...STATUS_REGISTROS]);
        }
    }, [filters.carteiras, filters.dataAquisicao]);

    const handleSearch = () => {
        setCurrentPage(0);
        fetchRegistros();
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (size: number) => {
        setPageSize(size);
        setCurrentPage(0);
    };

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setCurrentPage(0);
    };

    const handleLiquidar = (contratoIds: number[]) => {
        const contratosFiltrados = registros
            .filter(r => contratoIds.includes(r.idContrato) && podeSerLiquidado(computeDisplayStatus(r) as any))
            .map(r => ({ id: r.idContrato, numero_contrato: r.numeroContrato }));
        
        if (contratosFiltrados.length === 0) {
            alert('Nenhum contrato válido para liquidação foi selecionado.');
            return;
        }
        
        setContratosToLiquidar(contratosFiltrados);
        setShowLiquidarModal(true);
    };

    const handleConfirmLiquidar = async () => {
        const contratoIds = contratosToLiquidar.map(c => c.id);
        await registrosService.liquidarContratos(contratoIds);
    };

    const handleCloseLiquidarModal = () => {
        setShowLiquidarModal(false);
        setContratosToLiquidar([]);
    };

    const handleCancelar = (contratoIds: number[]) => {
        const registrosFiltrados = registros
            .filter(r => contratoIds.includes(r.idContrato) && podeSerCancelado(computeDisplayStatus(r) as any))
            .map(r => ({ id: r.idContrato, idRegistradoraContrato: r.idRegistradoraContrato, numeroContrato: r.numeroContrato }));

        if (registrosFiltrados.length === 0) {
            alert('Nenhum registro válido para cancelamento foi selecionado.');
            return;
        }

        setRegistrosToCancel(registrosFiltrados);
        setShowCancelarModal(true);
    };

    const handleConfirmCancelar = async (razaoCancelamento: string) => {
        const ids = registrosToCancel.map(r => r.idRegistradoraContrato);
        await registrosService.cancelarAtivos(ids, '', razaoCancelamento);
    };

    const handleCloseCancelarModal = () => {
        setShowCancelarModal(false);
        setRegistrosToCancel([]);
    };

    const handleLiquidarParcelas = async (parcelas: Array<{ id_parcela: number; vl_pago: number; dtMovimento: string }>) => {
        try {
            await registrosService.liquidarParcelas(parcelas);
        } catch (err) {
            console.error('Erro ao liquidar parcelas:', err);
            throw err;
        }
    };

    useEffect(() => {
        fetchCarteiras();
    }, [fetchCarteiras]);

    useEffect(() => {
        fetchAvailableStatuses();
    }, [fetchAvailableStatuses]);

    useEffect(() => {
        if (filters.status && !availableStatuses.includes(filters.status as StatusRegistro)) {
            setFilters(prev => ({ ...prev, status: '' }));
        }
    }, [availableStatuses]);

    useEffect(() => {
        fetchRegistros();
    }, []);


    useEffect(() => {
        if (currentPage > 0 || sortField) {
            fetchRegistros();
        }
    }, [currentPage, pageSize, sortField, sortDirection]); 
    
    return (
        <ErrorBoundary>
            <div className="registros-container">
                <RegistrosFilters
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onClearFilters={clearFilters}
                    onSearch={handleSearch}
                    carteiras={carteiras}
                    loadingCarteiras={loadingCarteiras}
                    availableStatuses={availableStatuses}
                />

                {error && (
                    <div className="error-message">
                        <p>Erro: {error}</p>
                    </div>
                )}

                <RegistrosTable 
                    data={registros} 
                    loading={loading}
                    onSort={handleSort}
                    sortField={sortField}
                    sortDirection={sortDirection}
                    onLiquidar={handleLiquidar}
                    onLiquidarParcelas={handleLiquidarParcelas}
                    onCancelar={handleCancelar}
                    onRefresh={fetchRegistros}
                />

                {!loading && registros && registros.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        pageSize={pageSize}
                        totalElements={totalElements}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                    />
                )}

                <LiquidarContratosModal
                    show={showLiquidarModal}
                    onClose={handleCloseLiquidarModal}
                    onConfirm={handleConfirmLiquidar}
                    contratos={contratosToLiquidar}
                    onSuccess={fetchRegistros}
                />
                <CancelarRegistrosModal
                    show={showCancelarModal}
                    onClose={handleCloseCancelarModal}
                    onConfirm={handleConfirmCancelar}
                    registros={registrosToCancel}
                    onSuccess={fetchRegistros}
                />
            </div>
        </ErrorBoundary>
    )
}

export default Registros;
